import { useEffect, useState } from "react";
import type { EtapaProceso, PlantillaProcesoDetalle } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Cargando } from "../../components/Cargando";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { EtapaTarjetaVisual, type TogglesVisualizacion } from "./EtapaTarjetaVisual";

const TOGGLES_TODO: TogglesVisualizacion = {
  responsables: true,
  contadores: true,
  automatizaciones: true,
  tiempoObjetivo: true,
  checklist: true,
};

// Convierte una etapa de plantilla (sin id real, todavía no persistida) en
// una EtapaProceso "de mentira" sólo para reutilizar EtapaTarjetaVisual en
// modo soloLectura — evita construir un segundo componente de tarjeta.
function etapaDePlantilla(e: PlantillaProcesoDetalle["etapas"][number], indice: number): EtapaProceso {
  return {
    id: `preview-${indice}`,
    tenant_id: "",
    proceso_id: "",
    nombre: e.nombre,
    color: e.color ?? null,
    orden: indice,
    responsable_usuario_id: null,
    tiempo_objetivo_dias: e.tiempoObjetivoDias ?? null,
    descripcion: e.descripcion ?? null,
    activo: true,
    creado_en: "",
    checklist_count: e.checklist.length,
    documentos_count: e.documentosSugeridos.length,
    automatizaciones_count: e.automatizaciones.length,
  };
}

export function PlantillaVistaPrevia({
  clave,
  onCerrar,
  onDuplicar,
  duplicando,
}: {
  clave: string;
  onCerrar: () => void;
  onDuplicar: (clave: string) => void;
  duplicando: boolean;
}) {
  const { mostrar } = useToast();
  const [detalle, setDetalle] = useState<PlantillaProcesoDetalle | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    setCargando(true);
    api
      .get<{ plantilla: PlantillaProcesoDetalle }>(`/procesos/plantillas/${clave}`)
      .then((d) => setDetalle(d.plantilla))
      .catch((err) => mostrar(err instanceof ErrorApi ? err.message : "No se pudo cargar la plantilla.", "error"))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  return (
    <Modal titulo="Vista previa de la plantilla" abierto onCerrar={onCerrar} ancho="max-w-3xl">
      {cargando || !detalle ? (
        <Cargando />
      ) : (
        <div className="space-y-5">
          <div>
            <h3 className="text-base font-semibold text-texto">{detalle.nombre}</h3>
            <p className="mt-1 text-sm text-texto-secundario">{detalle.descripcion}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {detalle.areaJuridica && <Insignia texto={detalle.areaJuridica} />}
              {detalle.complejidad && <Insignia texto={`Complejidad: ${detalle.complejidad}`} />}
              <Insignia texto={`${detalle.cantidadEtapas} etapas`} />
              {detalle.duracionEstimadaDias != null && <Insignia texto={`~${detalle.duracionEstimadaDias} días`} />}
            </div>
            {detalle.usuariosRecomendados && (
              <p className="mt-2 text-xs text-texto-secundario">Usuarios recomendados: {detalle.usuariosRecomendados}</p>
            )}
          </div>

          <div className="flex flex-wrap gap-3 overflow-x-auto pb-2">
            {detalle.etapas.map((etapa, i) => (
              <EtapaTarjetaVisual
                key={i}
                etapa={etapaDePlantilla(etapa, i)}
                indice={i}
                vistaCompacta={false}
                mostrar={TOGGLES_TODO}
                puedeAdministrar={false}
                soloLectura
              />
            ))}
          </div>

          <div className="max-h-56 space-y-3 overflow-y-auto border-t border-borde pt-3 text-sm">
            {detalle.etapas
              .filter((e) => e.checklist.length > 0 || e.documentosSugeridos.length > 0 || e.automatizaciones.length > 0)
              .map((etapa, i) => (
                <div key={i}>
                  <p className="font-medium text-texto">{etapa.nombre}</p>
                  {etapa.checklist.length > 0 && (
                    <p className="text-xs text-texto-secundario">
                      Checklist: {etapa.checklist.map((c) => c.texto).join(", ")}
                    </p>
                  )}
                  {etapa.documentosSugeridos.length > 0 && (
                    <p className="text-xs text-texto-secundario">Documentos: {etapa.documentosSugeridos.join(", ")}</p>
                  )}
                  {etapa.automatizaciones.length > 0 && (
                    <p className="text-xs text-texto-secundario">
                      Automatizaciones: {etapa.automatizaciones.map((a) => a.descripcion).join(", ")}
                    </p>
                  )}
                </div>
              ))}
          </div>

          <div className="flex justify-end gap-2 border-t border-borde pt-4">
            <Boton variante="secundario" onClick={onCerrar} disabled={duplicando}>
              Cerrar
            </Boton>
            <Boton onClick={() => onDuplicar(clave)} cargando={duplicando}>
              Duplicar como nuevo proceso
            </Boton>
          </div>
        </div>
      )}
    </Modal>
  );
}
